/* Local mesh repulsion. No scroll cancellation or shopping-component changes. */
if (!customElements.get('ggps-security-pattern')) {
  customElements.define('ggps-security-pattern', class extends HTMLElement {
    connectedCallback() {
      this.cleanup();
      if (this.dataset.interactive !== 'true') return;
      this.controller = new AbortController();
      const options = { passive: true, signal: this.controller.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'ggps-security-pattern__canvas';
      this.canvas.setAttribute('aria-hidden', 'true');
      this.ctx = this.canvas.getContext('2d');
      if (!this.ctx) return;
      this.append(this.canvas);
      this.power = this.targetPower = 0;
      this.x = this.y = this.targetX = this.targetY = 0;
      this.visible = true;
      this.lineColor = this.dataset.lineColor || '#a7cde3';
      this.loadLogo();
      this.motion.addEventListener('change', () => {
        this.reset(true);
        this.classList.toggle('is-repulsion-ready', !this.motion.matches);
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this);
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (!this.visible) this.reset(true);
      });
      this.intersectionObserver.observe(this);
      const surface = this.hasAttribute('data-page-pattern')
        ? this.closest('main') || this.parentElement
        : this.parentElement;
      const point = event => {
        if (this.motion.matches) { this.reset(true); return; }
        if (event.target.closest('a,button,input,select,textarea,summary')) return;
        const box = this.getBoundingClientRect();
        clearTimeout(this.settle);
        this.targetX = event.clientX - box.left;
        this.targetY = event.clientY - box.top;
        if (this.power < .01) { this.x = this.targetX; this.y = this.targetY; }
        this.targetPower = 1;
        this.wake();
      };
      surface.addEventListener('pointermove', event => {
        if (event.pointerType !== 'touch') point(event);
      }, options);
      surface.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch') this.touchStart = { x: event.clientX, y: event.clientY };
      }, options);
      surface.addEventListener('pointerup', event => {
        if (event.pointerType !== 'touch' || !this.touchStart) return;
        const distance = Math.hypot(event.clientX - this.touchStart.x, event.clientY - this.touchStart.y);
        this.touchStart = null;
        if (distance > 12) return;
        point(event);
        this.settle = setTimeout(() => this.reset(), 600);
      }, options);
      surface.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'touch') this.reset();
      }, options);
      surface.addEventListener('pointercancel', () => { this.touchStart = null; this.reset(); }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.reset(true);
      }, options);
      if (this.hasAttribute('data-page-pattern')) {
        // Capture both the desktop .page-wrapper scroller and mobile document
        // scrolling. The backdrop stays still; clear the previous pointer hole.
        document.addEventListener('scroll', () => {
          this.touchStart = null;
          this.reset(true);
        }, { ...options, capture: true });
      }
      this.resize();
    }
    loadLogo() {
      if (!this.dataset.logoSrc) return;
      const image = new Image();
      this.logoImage = image;
      image.onload = () => {
        if (!this.isConnected || this.logoImage !== image) return;
        // Frame the original mark without its transparent margins. Preserve
        // its 563:466 proportions and tint only its existing opaque pixels.
        const logo = document.createElement('canvas');
        logo.width = 563; logo.height = 466;
        const context = logo.getContext('2d');
        if (!context) return;
        context.drawImage(image, 342, 399, 563, 466, 0, 0, 563, 466);
        context.globalCompositeOperation = 'source-in';
        context.fillStyle = this.lineColor;
        context.fillRect(0, 0, 563, 466);
        this.logo = logo;
        this.draw();
      };
      image.src = this.dataset.logoSrc;
    }
    resize() {
      const box = this.getBoundingClientRect();
      this.width = box.width; this.height = box.height;
      if (!this.width || !this.height) return;
      const dpr = Math.min(devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(this.width * dpr);
      this.canvas.height = Math.round(this.height * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const scale = (this.height + 60) / 600;
      const tileWidth = 1330 * scale;
      let start = (this.width - tileWidth) / 2;
      while (start > 0) start -= tileWidth;
      this.lines = [];
      for (let tile = start; tile < this.width; tile += tileWidth) {
        for (let family = 0; family < 2; family++) {
          for (let i = -22; i <= 60; i++) {
            const line = [];
            for (let j = 0; j <= 167; j++) {
              const sx = Math.min(j * 8, 1330);
              const phase = sx * Math.PI * 2 / 1330;
              const sy = 300 + (i - 20) * 15
                + (family === 0 ? 175 : -175) * Math.sin(phase + i * .043 + (family === 0 ? 0 : .7))
                + 38 * Math.sin(phase * 2 - i * .026);
              line.push(tile + sx * scale, sy * scale - 30);
            }
            this.lines.push(new Float32Array(line));
          }
        }
      }
      this.lineWidth = .85 * scale;
      this.radius = this.width < 750 ? 115 : 170;
      this.draw();
      this.classList.toggle('is-repulsion-ready', !this.motion.matches);
    }
    // Smooth compact radial field: points outside the radius remain unchanged.
    repel(x, y) {
      const dx = x - this.x, dy = y - this.y;
      const distance = Math.hypot(dx, dy);
      if (!distance || distance >= this.radius || !this.power) return [x, y];
      const offset = this.radius * .45 * this.power * (1 - distance / this.radius) ** 2;
      return [x + dx / distance * offset, y + dy / distance * offset];
    }
    draw() {
      if (!this.ctx || !this.lines) return;
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.width, this.height);
      ctx.strokeStyle = this.lineColor;
      ctx.lineWidth = this.lineWidth;
      ctx.beginPath();
      for (const line of this.lines) {
        for (let i = 0; i < line.length; i += 2) {
          const [x, y] = this.repel(line[i], line[i + 1]);
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
      if (this.logo && this.power > .001) {
        const width = this.radius * .58 * this.power;
        const height = width * 466 / 563;
        ctx.save();
        // The same canvas opacity applies to both lines and logo. Only the
        // entrance/exit adds a fade, reaching identical opacity when settled.
        ctx.globalAlpha = this.power;
        ctx.drawImage(this.logo, this.x - width / 2, this.y - height / 2, width, height);
        ctx.restore();
      }
    }
    wake() {
      if (!this.frame && this.visible && !this.motion.matches) {
        this.lastTime = performance.now();
        this.frame = requestAnimationFrame(time => this.tick(time));
      }
    }
    tick(time) {
      this.frame = 0;
      if (this.motion.matches) { this.reset(true); return; }
      if (!this.isConnected || !this.visible) return;
      const easing = 1 - Math.exp(-Math.min(time - this.lastTime, 50) / 75);
      this.lastTime = time;
      this.power += (this.targetPower - this.power) * easing;
      this.x += (this.targetX - this.x) * easing;
      this.y += (this.targetY - this.y) * easing;
      const settled = Math.abs(this.targetPower - this.power) < .002
        && Math.abs(this.targetX - this.x) + Math.abs(this.targetY - this.y) < .2;
      if (settled) this.power = this.targetPower;
      this.draw();
      if (!settled) this.frame = requestAnimationFrame(t => this.tick(t));
    }
    reset(immediate = false) {
      clearTimeout(this.settle);
      this.targetPower = 0;
      if (immediate) {
        cancelAnimationFrame(this.frame); this.frame = 0; this.power = 0;
        this.draw();
      } else if (this.motion) this.wake();
    }
    cleanup() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      cancelAnimationFrame(this.frame); this.frame = 0;
      clearTimeout(this.settle);
      this.canvas?.remove();
      if (this.logoImage) this.logoImage.onload = null;
      this.logoImage = null;
      this.logo = null;
      this.classList.remove('is-repulsion-ready');
      this.touchStart = null;
    }
    disconnectedCallback() { this.cleanup(); }
  });
}
